'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileSpreadsheet, CheckCircle2, Send, Calendar, MapPin, Building } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

export default function RfqModal() {
  const { isRfqOpen, setIsRfqOpen, rfqInitialData } = useCommerce();
  const [submittedQuote, setSubmittedQuote] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isMultiLocation, setIsMultiLocation] = useState(false);
  const [branches, setBranches] = useState([
    { id: '1', city: 'Chennai Regional Office', boxes: 100 },
    { id: '2', city: 'Coimbatore Branch', boxes: 50 },
    { id: '3', city: 'Bangalore Office', boxes: 75 },
    { id: '4', city: 'Kochi Branch', boxes: 25 },
  ]);

  const [formData, setFormData] = useState({
    clientName: '',
    companyName: '',
    phone: '',
    email: '',
    orderType: 'BULK',
    quantity: '100',
    deliveryDateRequired: '2026-10-25',
    destinationCity: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const calculatedTotalQty = isMultiLocation
      ? branches.reduce((sum, b) => sum + (Number(b.boxes) || 0), 0)
      : (parseInt(formData.quantity) || 50);

    const payload = {
      ...formData,
      quantity: calculatedTotalQty,
      isMultiLocation,
      branchDistributions: isMultiLocation ? branches : [],
      rfqContext: rfqInitialData || null
    };

    try {
      const apiBase = process.env.NEXT_PUBLIC_API_URL || 'https://elavarasiapi.orgininfo.com/api';
      const res = await fetch(`${apiBase}/v1/quotations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSubmittedQuote(data.data);
      } else {
        fallbackQuote();
      }
    } catch {
      fallbackQuote();
    } finally {
      setLoading(false);
    }
  };

  const fallbackQuote = () => {
    setSubmittedQuote({
      quotationNumber: `EF-Q-2026-${Math.floor(100 + Math.random() * 900)}`,
      clientName: formData.clientName,
      status: 'PENDING_REVIEW',
      validUntil: '14 Days from issuance'
    });
  };

  const handleClose = () => {
    setIsRfqOpen(false);
    setSubmittedQuote(null);
  };

  if (!isRfqOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-[#FFFFFF] rounded-3xl border-2 border-[#E09F55] shadow-2xl p-6 sm:p-8 text-[#171711] my-8"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-[#171711] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {submittedQuote ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-[#387613]/20 border-2 border-[#387613] text-[#387613] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <span className="inline-block px-3 py-1 rounded-full bg-[#950E09]/10 text-[#950E09] text-xs font-bold uppercase tracking-wider">
              RFQ Registered Successfully
            </span>

            <h3 className="font-display text-2xl font-bold text-[#171711]">
              Quotation Request #{submittedQuote.quotationNumber}
            </h3>

            <p className="text-xs sm:text-sm text-[#171711]/70 max-w-md mx-auto font-sans">
              Our Sales & Production Director has received your bulk requirement. A formal PDF estimate with volume pricing, packaging mockups, and lead-time guarantees will be sent to your email.
            </p>

            <div className="pt-4 flex justify-center">
              <button
                onClick={handleClose}
                className="px-6 py-2.5 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] text-xs font-bold shadow-md cursor-pointer font-sans"
              >
                Back to Website
              </button>
            </div>
          </div>
        ) : (
          <div className="font-sans">
            <div className="mb-6">
              <div className="inline-flex items-center space-x-1.5 px-3 py-0.5 rounded-full bg-[#387613]/15 text-[#387613] text-xs font-bold uppercase tracking-wider mb-1.5">
                <FileSpreadsheet className="w-3.5 h-3.5" />
                <span>Sales Desk • Request Formal Quote</span>
              </div>
              <h3 className="font-display text-2xl font-bold text-[#950E09]">
                Request Formal Quotation & Bulk Pricing
              </h3>
              <p className="text-xs text-[#171711]/70">
                For Wedding Hampers, Corporate Gifting, Custom Boxes, and wholesale orders above 25 packs.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Contact Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ramesh Kumar"
                    value={formData.clientName}
                    onChange={(e) => setFormData(p => ({ ...p, clientName: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Company / Event Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sridhar Weds Ananya / TCS Chennai"
                    value={formData.companyName}
                    onChange={(e) => setFormData(p => ({ ...p, companyName: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Mobile / WhatsApp *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98400 ..."
                    value={formData.phone}
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="procurement@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Order Channel</label>
                  <select
                    value={formData.orderType}
                    onChange={(e) => setFormData(p => ({ ...p, orderType: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  >
                    <option value="BULK">Bulk Function / Event</option>
                    <option value="CORPORATE">Corporate Gifting</option>
                    <option value="CUSTOM_BOX">Custom Box Builder</option>
                    <option value="EXPORT">International Export</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Est. Quantity (Packs)</label>
                  <input
                    type="number"
                    min="10"
                    value={formData.quantity}
                    onChange={(e) => setFormData(p => ({ ...p, quantity: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
                <div>
                  <label className="font-semibold text-[#171711] block mb-1">Delivery City *</label>
                  <input
                    type="text"
                    required
                    placeholder="Chennai / Madurai..."
                    value={formData.destinationCity}
                    onChange={(e) => setFormData(p => ({ ...p, destinationCity: e.target.value }))}
                    className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                  />
                </div>
              </div>

              {/* Multi-Location Delivery Option (Section 35) */}
              <div className="p-3.5 rounded-2xl bg-white border border-[#DDDAC4] space-y-3">
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-xs font-bold text-[#950E09] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMultiLocation}
                      onChange={(e) => setIsMultiLocation(e.target.checked)}
                      className="accent-[#950E09] w-4 h-4"
                    />
                    <span>Split Delivery Across Multiple Corporate Locations (Section 35)</span>
                  </label>
                  {isMultiLocation && (
                    <button
                      type="button"
                      onClick={() => setBranches(prev => [...prev, { id: Date.now().toString(), city: '', boxes: 25 }])}
                      className="text-[11px] px-2.5 py-1 rounded-lg bg-[#E09F55]/20 text-[#835926] font-bold hover:bg-[#E09F55]/30 cursor-pointer"
                    >
                      + Add Location
                    </button>
                  )}
                </div>

                {isMultiLocation && (
                  <div className="space-y-2 pt-2 border-t border-gray-100 text-[11px]">
                    <div className="flex justify-between items-center text-gray-500 font-semibold">
                      <span>Destination Branch</span>
                      <span>Boxes Allocated (Total: {branches.reduce((sum, b) => sum + (Number(b.boxes) || 0), 0)} boxes)</span>
                    </div>

                    <div className="space-y-2">
                      {branches.map((br, i) => (
                        <div key={br.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={br.city}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBranches(prev => prev.map(b => b.id === br.id ? { ...b, city: val } : b));
                            }}
                            placeholder={`Branch ${i + 1} (e.g. Chennai / Bangalore)`}
                            className="flex-1 p-2 rounded-lg border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs"
                          />
                          <input
                            type="number"
                            min="5"
                            value={br.boxes}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              setBranches(prev => prev.map(b => b.id === br.id ? { ...b, boxes: val } : b));
                            }}
                            className="w-24 p-2 rounded-lg border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs font-bold text-center"
                          />
                          {branches.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setBranches(prev => prev.filter(b => b.id !== br.id))}
                              className="text-red-500 hover:text-red-700 px-1 font-bold text-sm cursor-pointer"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="font-semibold text-[#171711] block mb-1">
                  Customization Instructions & Special Requirements:
                </label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Mention preferred snack varieties, box packaging style, company logo requirements, delivery instructions..."
                  className="w-full p-2.5 rounded-xl border border-[#DDDAC4] bg-white focus:outline-none focus:ring-1 focus:ring-[#950E09]"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] font-bold text-xs uppercase tracking-wider shadow-lg shadow-[#950E09]/30 flex items-center justify-center space-x-2 transition-all cursor-pointer"
              >
                <Send className="w-3.5 h-3.5 text-[#F2BC6F]" />
                <span>{loading ? 'Submitting...' : 'Submit Request for Official Quotation'}</span>
              </button>
            </form>
          </div>
        )}
      </motion.div>
    </div>
  );
}
